<template>
  <div class="pagination-strip">
    <div class="grey-custom-pagination__wrapper pagination-wrapper">
      <div class="col rows-per-page">
        <span class="footer__row-count__label">{{ rowsPerPageText }}</span>

        <el-select
          name="perPageSelect"
          v-model="currentPerPage"
          @change="customPerPageChanged"
          placeholder="Per page"
          style="width: 80px"
        >
          <el-option
            class="pagination-option"
            v-for="(option, idx) in rowsPerPageOptions"
            :key="'rows-dropdown-option-' + idx"
            :value="option"
            :label="option"
          ></el-option>
          <el-option v-if="paginateDropdownAllowAll" :value="total">{{
            allText
          }}</el-option>
        </el-select>
        <div class="grey-custom-pagination__info">
          Showing {{ paginatedInfo }}
        </div>
      </div>
      <div class="grey-custom-pagination vgt-pull-right">
        <div class="grey-custom-pagination__wrapper">
          <div
            class="pagination_item"
            :class="{ disabled: !prevIsPossible }"
            @click.prevent.stop="jumpToFirst"
          >
            <span class="fa fa-angle-double-left"></span>
          </div>
          <div
            class="pagination_item"
            :class="{ disabled: !prevIsPossible }"
            @click.prevent.stop="previousPage"
          >
            <span class="fa fa-angle-left"></span>
          </div>
          <div class="pagination_item active">
            <span>{{ currentPage }}</span>
          </div>
          <div
            class="pagination_item"
            :class="{ disabled: !nextIsPossible }"
            v-if="nextIsPossible"
            @click.prevent.stop="nextPage"
          >
            <span>{{ currentPage + 1 }}</span>
          </div>
          <div
            class="pagination_item"
            :class="{ disabled: !nextIsPossible }"
            @click.prevent.stop="nextPage"
          >
            <span class="fa fa-angle-right"></span>
          </div>
          <div
            class="pagination_item"
            :class="{ disabled: !nextIsPossible }"
            @click.prevent.stop="jumpToLast"
          >
            <span class="fa fa-angle-double-right"></span>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import cloneDeep from "lodash.clonedeep";
import { mapGetters } from "vuex";

const DEFAULT_ROWS_PER_PAGE_DROPDOWN = [20,50, 60, 70, 80, 100, 200];

export default {
  name: "GreyPagination",
  props: {
    styleClass: { default: "table table-bordered" },
    total: { default: null },
    perPage: {},
    rtl: { default: false },
    customRowsPerPageDropdown: {
      default() {
        return [];
      }
    },

    paginateDropdownAllowAll: { default: false },
    mode: { default: "records" },
    // text options
    nextText: { default: "Next" },
    prevText: { default: "Prev" },
    rowsPerPageText: { default: "Rows per page:" },
    ofText: { default: "of" },
    pageText: { default: "page" },
    allText: { default: "All" },
    currentPage: { default: 1 },
    pageChanged: {
      type: Function
    },
    perPageChanged: {
      type: Function
    }
  },

  data: () => ({
    prevPage: 0,
    currentPerPage: 200,
    rowsPerPageOptions: []
  }),

  watch: {
    perPage: {
      handler(newValue, oldValue) {
        // console.log({
        //   newValue,
        //   oldValue
        // })
        this.handlePerPage();
        this.customPerPageChanged();
      },
      immediate: true
    },

    customRowsPerPageDropdown() {
      this.handlePerPage();
    }
  },

  computed: {
    ...mapGetters(["isSidebarCollapsed"]),
    // Number of pages
    pagesCount() {
      const quotient = Math.floor(this.total / this.currentPerPage);
      const remainder = this.total % this.currentPerPage;

      return remainder === 0 ? quotient : quotient + 1;
    },

    // Current displayed items
    paginatedInfo() {
      let first = (this.currentPage - 1) * this.currentPerPage + 1;
      const last = Math.min(this.total, this.currentPage * this.currentPerPage);

      if (last === 0) {
        first = 0;
      }

      // let paginatedInfoString = `${first} - ${last} ${this.ofText} ${this.total}`;
      this.$emit("paginated-info", {
        first,
        last,
        ofText: this.ofText,
        total: this.total
      });
      return `${first} - ${last} ${this.ofText} ${this.total}`;
    },

    // Get last page
    // lastPage(){

    // },

    // Can go to next page
    nextIsPossible() {
      return this.currentPage < this.pagesCount;
    },

    // Can go to previous page
    prevIsPossible() {
      return this.currentPage > 1;
    }
  },

  methods: {
    // Change current page
    changePage(pageNumber, emit = true) {
      if (
        pageNumber > 0 &&
        this.total > this.currentPerPage * (pageNumber - 1)
      ) {
        this.prevPage = this.currentPage;
        this.currentPage = pageNumber;
        if (emit) this.customPageChanged();
      }
    },

    // Go to next page
    nextPage() {
      if (this.nextIsPossible) {
        this.prevPage = this.currentPage;
        ++this.currentPage;
        this.customPageChanged();
      }
    },

    jumpToFirst() {
      if (this.prevIsPossible) {
        this.prevPage = this.currentPage;

        this.currentPage = 1;
        this.customPageChanged();
      }
    },
    jumpToLast() {
      if (this.nextIsPossible) {
        this.prevPage = this.currentPage;

        this.currentPage = this.pagesCount;
        this.customPageChanged();
      }
    },

    // Go to previous page
    previousPage() {
      if (this.prevIsPossible) {
        this.prevPage = this.currentPage;
        --this.currentPage;
        this.customPageChanged();
      }
    },

    // customPerPageChange(customPerPage) {
    //   this.customPerPageChanged({currentPerPage: customPerPage});
    // }
    // Indicate page changing
    customPageChanged() {
      this.pageChanged({ currentPage: this.currentPage });
      // this.$emit('page-changed', {
      //   currentPage: this.currentPage,
      //   prevPage: this.prevPage,
      // });
    },

    // Indicate per page changing
    customPerPageChanged() {
      // go back to first page
      this.perPageChanged({ currentPerPage: this.currentPerPage });
      // this.$emit('per-page-changed', { currentPerPage: this.currentPerPage });
      // this.changePage(1, false);
    },

    // Handle per page changing
    handlePerPage() {
      //* if there's a custom dropdown then we use that
      if (
        this.customRowsPerPageDropdown !== null &&
        Array.isArray(this.customRowsPerPageDropdown) &&
        this.customRowsPerPageDropdown.length !== 0
      ) {
        this.rowsPerPageOptions = this.customRowsPerPageDropdown;
      } else {
        //* otherwise we use the default rows per page dropdown
        this.rowsPerPageOptions = cloneDeep(DEFAULT_ROWS_PER_PAGE_DROPDOWN);
      }

      if (this.perPage) {
        this.currentPerPage = this.perPage;
        // if perPage doesn't already exist, we add it
        let found = false;
        for (let i = 0; i < this.rowsPerPageOptions.length; i++) {
          if (this.rowsPerPageOptions[i] === this.perPage) {
            found = true;
          }
        }
        if (!found && this.perPage !== -1) {
          this.rowsPerPageOptions.unshift(this.perPage);
        }
      } else {
        // reset to default
        this.currentPerPage = 50;
      }
    }
  },

  mounted() {
    if (this.pagesCount >= this.currentPage) {
      this.changePage(this.currentPage);
    }
  },

  components: {
    // 'pagination-page-info': VgtPaginationPageInfo,
  }
};
</script>

<style lang="scss">
.pagination-strip {
  position: fixed;
  bottom: 0px;
  width: calc(100vw - 390px);
}
.grey-custom-pagination__wrapper {
  display: flex;
  align-items: center;
  justify-content: space-between;
}
.footer__row-count__label,
.grey-custom-pagination__info,
.pagination-option {
  font-size: 12px;
}
.pagination_item {
  cursor: pointer;
  border-radius: 50%;
  width: 41px;
  height: 41px;
  border: 1px solid #1a93ca;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #1a93ca;
  margin: 5px;
  &.disabled {
    color: grey;
    border-color: grey;
  }
  &.active {
    color: #fff;
    background: #1a93ca;
  }

  span {
    font-size: 12px;
  }
}

.grey-custom-pagination__wrapper {
  display: flex;
  margin: 0 auto;
}

.pagination-wrapper {
  display: flex;
  justify-content: space-between;
}
.rows-per-page {
  display: flex;
  align-items: center;
}
.rows-per-page .el-select {
  margin: 0px 8px;
}
.grey-custom-pagination__info {
  color: #a5a5a5;
}
/* .pagination_item {
  border: 1px solid #A5A5A5;
}
.pagination_item.disabled {
  border: 1px solid #A5A5A5;
  color: #A5A5A5;
} */
</style>



// WEBPACK FOOTER //
// src/components/pages/kitchen/grey-pagination.vue